// Kabbalistic data tables for the Hebrew natal chart system
// Sources: Sefer Yetzirah, Zohar, Tanya, traditional minhag

export interface MazalInfo {
  hebrewName: string;
  transliteration: string;
  spanishName: string;
  symbol: string;
  tribe: TribeInfo;
  letter: LetterInfo;
  sefirah: SefirahInfo;
  planet: PlanetInfo;
  element: string;
  qualities: string[];
  tikun: string;
}

export interface TribeInfo {
  name: string;
  hebrew: string;
  englishName: string;
  qualities: string[];
  stone: string;
}

export interface LetterInfo {
  letter: string;
  name: string;
  numericalValue: number;
  meaning: string;
  spiritualForce: string;
}

export interface SefirahInfo {
  name: string;
  hebrew: string;
  meaning: string;
  divine: string;
  bodyPart: string;
  color: string;
  qualities: string[];
}

export interface PlanetInfo {
  name: string;
  hebrew: string;
  sefirahConnection: string;
}

// Month keys (1=Nisan, 7=Tishrei) matching @hebcal/core output
export const MAZALOT: Record<number, MazalInfo> = {
  1: { // Nisan — Taleh (Aries)
    hebrewName: 'טָלֶה',
    transliteration: 'Taleh',
    spanishName: 'Aries',
    symbol: '♈',
    tribe: {
      name: 'Yehudá',
      hebrew: 'יְהוּדָה',
      englishName: 'Judah',
      qualities: ['Liderazgo', 'Valentía', 'Nobleza real', 'Determinación'],
      stone: 'Rubí'
    },
    letter: {
      letter: 'ה',
      name: 'Heh',
      numericalValue: 5,
      meaning: 'Ventana hacia lo divino, revelación',
      spiritualForce: 'Capacidad de recibir la luz superior y de revelar lo oculto'
    },
    sefirah: {
      name: 'Gevurá',
      hebrew: 'גְּבוּרָה',
      meaning: 'Fortaleza',
      divine: 'Din — Juicio divino',
      bodyPart: 'Brazo izquierdo',
      color: '#dc2626',
      qualities: ['Disciplina', 'Límites sagrados', 'Fuerza interior', 'Justicia']
    },
    planet: { name: 'Marte', hebrew: 'מַאְדִּים', sefirahConnection: 'Gevurá' },
    element: 'Fuego',
    qualities: ['Iniciativa pionera', 'Liderazgo natural', 'Impulsividad creativa', 'Renovación espiritual'],
    tikun: 'Transformar la impaciencia en acción propositiva; liderar con compasión, no solo con fuerza'
  },

  2: { // Iyar — Shor (Taurus)
    hebrewName: 'שׁוֹר',
    transliteration: 'Shor',
    spanishName: 'Tauro',
    symbol: '♉',
    tribe: {
      name: 'Yissachar',
      hebrew: 'יִשָּׂשכָר',
      englishName: 'Issachar',
      qualities: ['Sabiduría Torah', 'Perseverancia', 'Profundidad intelectual', 'Constancia'],
      stone: 'Zafiro'
    },
    letter: {
      letter: 'ו',
      name: 'Vav',
      numericalValue: 6,
      meaning: 'Gancho, conexión entre mundos',
      spiritualForce: 'Capacidad de conectar lo espiritual con lo material, unir cielo y tierra'
    },
    sefirah: {
      name: 'Netzaj',
      hebrew: 'נֶצַח',
      meaning: 'Victoria / Eternidad',
      divine: 'Tzvaot — Ejércitos divinos',
      bodyPart: 'Pierna derecha',
      color: '#16a34a',
      qualities: ['Persistencia', 'Creatividad artística', 'Amor incondicional', 'Naturaleza']
    },
    planet: { name: 'Venus', hebrew: 'נוֹגַהּ', sefirahConnection: 'Netzaj' },
    element: 'Tierra',
    qualities: ['Constancia fecunda', 'Paciencia profunda', 'Apreciación de la belleza', 'Materialidad sagrada'],
    tikun: 'Liberar el apego a lo material y descubrir que la verdadera riqueza es espiritual'
  },

  3: { // Sivan — Teomim (Gemini)
    hebrewName: 'תְּאוֹמִים',
    transliteration: 'Teomim',
    spanishName: 'Géminis',
    symbol: '♊',
    tribe: {
      name: 'Zevulún',
      hebrew: 'זְבוּלוּן',
      englishName: 'Zebulun',
      qualities: ['Comunicación', 'Comercio sagrado', 'Apoyo a la sabiduría', 'Adaptabilidad'],
      stone: 'Diamante'
    },
    letter: {
      letter: 'ז',
      name: 'Zayin',
      numericalValue: 7,
      meaning: 'Espada, distinción, Shabat',
      spiritualForce: 'Capacidad de discernir entre lo sagrado y lo profano, la mente que separa y unifica'
    },
    sefirah: {
      name: 'Hod',
      hebrew: 'הוֹד',
      meaning: 'Esplendor',
      divine: 'Tzvaot — Ejércitos divinos',
      bodyPart: 'Pierna izquierda',
      color: '#d97706',
      qualities: ['Gratitud', 'Elocuencia', 'Claridad comunicativa', 'Humildad']
    },
    planet: { name: 'Mercurio', hebrew: 'כּוֹכָב', sefirahConnection: 'Hod' },
    element: 'Aire',
    qualities: ['Mente brillante y ágil', 'Dualidad fructífera', 'Capacidad de síntesis', 'Curiosidad insaciable'],
    tikun: 'Unificar la mente dual hacia una verdad coherente; que la palabra sirva al alma'
  },

  4: { // Tammuz — Sartan (Cancer)
    hebrewName: 'סַרְטָן',
    transliteration: 'Sartan',
    spanishName: 'Cáncer',
    symbol: '♋',
    tribe: {
      name: 'Reuven',
      hebrew: 'רְאוּבֵן',
      englishName: 'Reuben',
      qualities: ['Primogenitura espiritual', 'Teshuva profunda', 'Sensibilidad emocional', 'Intuición'],
      stone: 'Cornalina roja'
    },
    letter: {
      letter: 'ח',
      name: 'Jet',
      numericalValue: 8,
      meaning: 'Vida, vitalidad, más allá del natural',
      spiritualForce: 'La vida que trasciende lo ordinario; conexión con la fuente de toda vitalidad'
    },
    sefirah: {
      name: 'Yesod',
      hebrew: 'יְסוֹד',
      meaning: 'Fundamento',
      divine: 'El Shaddai — Dios que sustenta',
      bodyPart: 'Órgano reproductivo / Canal',
      color: '#7c3aed',
      qualities: ['Canalización divina', 'Fecundidad', 'Pactos sagrados', 'Transmisión de luz']
    },
    planet: { name: 'Luna', hebrew: 'לְבָנָה', sefirahConnection: 'Yesod' },
    element: 'Agua',
    qualities: ['Profundidad emocional', 'Cuidado nutricio', 'Memoria ancestral', 'Intuición lunar'],
    tikun: 'Transformar el apego al pasado en sabiduría presente; nutrir desde el amor, no desde el miedo'
  },

  5: { // Av — Aryeh (Leo)
    hebrewName: 'אַרְיֵה',
    transliteration: 'Aryeh',
    spanishName: 'Leo',
    symbol: '♌',
    tribe: {
      name: 'Shimon',
      hebrew: 'שִׁמְעוֹן',
      englishName: 'Simeon',
      qualities: ['Celo sagrado', 'Intensidad', 'Justicia apasionada', 'Fuerza transformadora'],
      stone: 'Esmeralda'
    },
    letter: {
      letter: 'ט',
      name: 'Tet',
      numericalValue: 9,
      meaning: 'Serpiente, bien oculto en el interior',
      spiritualForce: 'El bien que se revela a través de la prueba; la luz escondida en la oscuridad'
    },
    sefirah: {
      name: 'Tiféret',
      hebrew: 'תִּפְאֶרֶת',
      meaning: 'Belleza / Armonía',
      divine: 'YHVH — El Nombre sagrado',
      bodyPart: 'Corazón',
      color: '#f59e0b',
      qualities: ['Equilibrio y armonía', 'Compasión real', 'Generosidad solar', 'Majestad']
    },
    planet: { name: 'Sol', hebrew: 'שֶׁמֶשׁ', sefirahConnection: 'Tiféret' },
    element: 'Fuego',
    qualities: ['Magnetismo natural', 'Generosidad regia', 'Creatividad solar', 'Corazón expandido'],
    tikun: 'Liderar desde el corazón, no el ego; que tu luz ilumine a otros sin eclipsarlos'
  },

  6: { // Elul — Betulah (Virgo)
    hebrewName: 'בְּתוּלָה',
    transliteration: 'Betulá',
    spanishName: 'Virgo',
    symbol: '♍',
    tribe: {
      name: 'Gad',
      hebrew: 'גָּד',
      englishName: 'Gad',
      qualities: ['Servicio dedicado', 'Habilidad práctica', 'Protección', 'Superación de obstáculos'],
      stone: 'Ágata'
    },
    letter: {
      letter: 'י',
      name: 'Yod',
      numericalValue: 10,
      meaning: 'Punto primordial, mano divina, esencia',
      spiritualForce: 'El punto de origen de toda creación; la conciencia enfocada que da forma al mundo'
    },
    sefirah: {
      name: 'Hod',
      hebrew: 'הוֹד',
      meaning: 'Esplendor',
      divine: 'Tzvaot — Ejércitos divinos',
      bodyPart: 'Pierna izquierda',
      color: '#d97706',
      qualities: ['Gratitud', 'Servicio perfecto', 'Análisis sagrado', 'Devoción genuina']
    },
    planet: { name: 'Mercurio', hebrew: 'כּוֹכָב', sefirahConnection: 'Hod' },
    element: 'Tierra',
    qualities: ['Perfeccionismo sagrado', 'Atención al detalle', 'Servicio genuino', 'Discernimiento agudo'],
    tikun: 'Transformar la crítica en compasión; el servicio perfecto nace del amor, no del control'
  },

  7: { // Tishrei — Moznayim (Libra)
    hebrewName: 'מֹאזְנַיִם',
    transliteration: 'Moznayim',
    spanishName: 'Libra',
    symbol: '♎',
    tribe: {
      name: 'Efraim',
      hebrew: 'אֶפְרַיִם',
      englishName: 'Ephraim',
      qualities: ['Fruto espiritual', 'Multiplicación', 'Gracia divina', 'Renovación'],
      stone: 'Ónice'
    },
    letter: {
      letter: 'ל',
      name: 'Lamed',
      numericalValue: 30,
      meaning: 'Aprendizaje, elevación, torre del corazón',
      spiritualForce: 'La aspiración del corazón hacia lo divino; el eterno aprendiz del alma'
    },
    sefirah: {
      name: 'Netzaj',
      hebrew: 'נֶצַח',
      meaning: 'Victoria / Eternidad',
      divine: 'Tzvaot — Ejércitos divinos',
      bodyPart: 'Pierna derecha',
      color: '#16a34a',
      qualities: ['Armonía relacional', 'Belleza equilibrada', 'Diplomacia', 'Justicia social']
    },
    planet: { name: 'Venus', hebrew: 'נוֹגַהּ', sefirahConnection: 'Netzaj' },
    element: 'Aire',
    qualities: ['Amor a la justicia', 'Búsqueda del equilibrio', 'Diplomacia natural', 'Perspectiva amplia'],
    tikun: 'Decidir sin indecisión; que la búsqueda del equilibrio no se convierta en evasión'
  },

  8: { // Cheshvan — Akrav (Scorpio)
    hebrewName: 'עַקְרָב',
    transliteration: 'Akrav',
    spanishName: 'Escorpio',
    symbol: '♏',
    tribe: {
      name: 'Menashé',
      hebrew: 'מְנַשֶּׁה',
      englishName: 'Manasseh',
      qualities: ['Transformación radical', 'Memoria sanada', 'Profundidad oculta', 'Resiliencia'],
      stone: 'Crisolito'
    },
    letter: {
      letter: 'נ',
      name: 'Nun',
      numericalValue: 50,
      meaning: 'Pez en el agua, fidelidad, caída y elevación',
      spiritualForce: 'La capacidad de sumergirse en las profundidades y regresar transformado; resiliencia del alma'
    },
    sefirah: {
      name: 'Gevurá',
      hebrew: 'גְּבוּרָה',
      meaning: 'Fortaleza',
      divine: 'Din — Juicio divino',
      bodyPart: 'Brazo izquierdo',
      color: '#dc2626',
      qualities: ['Transformación profunda', 'Poder interior', 'Lealtad absoluta', 'Penetración de lo oculto']
    },
    planet: { name: 'Marte', hebrew: 'מַאְדִּים', sefirahConnection: 'Gevurá' },
    element: 'Agua',
    qualities: ['Intensidad transformadora', 'Visión de lo oculto', 'Lealtad total', 'Poder regenerativo'],
    tikun: 'Transformar el poder del control en poder de servicio; que la intensidad sea fuente de luz'
  },

  9: { // Kislev — Keshet (Sagittarius)
    hebrewName: 'קֶשֶׁת',
    transliteration: 'Keshet',
    spanishName: 'Sagitario',
    symbol: '♐',
    tribe: {
      name: 'Binyamín',
      hebrew: 'בִּנְיָמִין',
      englishName: 'Benjamin',
      qualities: ['Confianza divina', 'Idealismo sagrado', 'Pureza de intención', 'Conexión con el templo'],
      stone: 'Jaspe'
    },
    letter: {
      letter: 'ס',
      name: 'Samej',
      numericalValue: 60,
      meaning: 'Sustento, apoyo circular, totalidad',
      spiritualForce: 'El apoyo divino que sostiene al alma en su camino; la providencia que rodea toda búsqueda'
    },
    sefirah: {
      name: 'Jesed',
      hebrew: 'חֶסֶד',
      meaning: 'Misericordia / Bondad',
      divine: 'El — Dios todopoderoso',
      bodyPart: 'Brazo derecho',
      color: '#2563eb',
      qualities: ['Expansión generosa', 'Visión elevada', 'Optimismo sagrado', 'Amor incondicional']
    },
    planet: { name: 'Júpiter', hebrew: 'צֶדֶק', sefirahConnection: 'Jesed' },
    element: 'Fuego',
    qualities: ['Filosofía del alma', 'Aventura espiritual', 'Optimismo profético', 'Expansión constante'],
    tikun: 'Aterrizar los ideales en acción concreta; que la flecha de la sabiduría alcance su blanco'
  },

  10: { // Tevet — Gdi (Capricorn)
    hebrewName: 'גְּדִי',
    transliteration: 'Gdi',
    spanishName: 'Capricornio',
    symbol: '♑',
    tribe: {
      name: 'Dan',
      hebrew: 'דָּן',
      englishName: 'Dan',
      qualities: ['Juicio sagrado', 'Organización estratégica', 'Aplicación de la ley', 'Determinación'],
      stone: 'Zafiro azul'
    },
    letter: {
      letter: 'ע',
      name: 'Ayin',
      numericalValue: 70,
      meaning: 'Ojo, fuente, percepción profunda',
      spiritualForce: 'La visión que penetra más allá de la superficie; el ojo del alma que ve la realidad divina'
    },
    sefirah: {
      name: 'Biná',
      hebrew: 'בִּינָה',
      meaning: 'Comprensión',
      divine: 'YHVH — Nombre de misericordia',
      bodyPart: 'Corazón / Palacio superior',
      color: '#1e40af',
      qualities: ['Comprensión profunda', 'Disciplina estructural', 'Maestría temporal', 'Sabiduría práctica']
    },
    planet: { name: 'Saturno', hebrew: 'שַׁבְּתַאי', sefirahConnection: 'Biná' },
    element: 'Tierra',
    qualities: ['Estructura sagrada', 'Paciencia estratégica', 'Maestría a largo plazo', 'Responsabilidad profunda'],
    tikun: 'Construir desde la misericordia, no solo desde el deber; que la estructura sirva a la libertad'
  },

  11: { // Shevat — Dli (Aquarius)
    hebrewName: 'דְּלִי',
    transliteration: 'Dli',
    spanishName: 'Acuario',
    symbol: '♒',
    tribe: {
      name: 'Asher',
      hebrew: 'אָשֵׁר',
      englishName: 'Asher',
      qualities: ['Bendición', 'Felicidad sagrada', 'Abundancia compartida', 'Servicio comunitario'],
      stone: 'Berilo'
    },
    letter: {
      letter: 'צ',
      name: 'Tzadi',
      numericalValue: 90,
      meaning: 'Tzadik — el justo, la justicia que fluye',
      spiritualForce: 'La rectitud del alma que transforma el entorno; la justicia que brota como un manantial'
    },
    sefirah: {
      name: 'Daat',
      hebrew: 'דַּעַת',
      meaning: 'Conocimiento íntimo',
      divine: 'El Elohim — unión de lo masculino y femenino divino',
      bodyPart: 'Nuca / Centro de unión',
      color: '#6d28d9',
      qualities: ['Conocimiento transformador', 'Innovación sagrada', 'Visión humanitaria', 'Unión de opuestos']
    },
    planet: { name: 'Saturno / Urano', hebrew: 'שַׁבְּתַאי', sefirahConnection: 'Daat' },
    element: 'Aire',
    qualities: ['Visión del futuro', 'Innovación espiritual', 'Fraternidad universal', 'Originalidad radical'],
    tikun: 'Conectar la visión universal con el amor personal; que la revolución comience en el corazón'
  },

  12: { // Adar — Dagim (Pisces)
    hebrewName: 'דָּגִים',
    transliteration: 'Dagim',
    spanishName: 'Piscis',
    symbol: '♓',
    tribe: {
      name: 'Naftalí',
      hebrew: 'נַפְתָּלִי',
      englishName: 'Naphtali',
      qualities: ['Velocidad espiritual', 'Elegancia', 'Gracia poética', 'Comunicación sublime'],
      stone: 'Amatista'
    },
    letter: {
      letter: 'ק',
      name: 'Kuf',
      numericalValue: 100,
      meaning: 'Mono, sagrado y profano, ciclo completo',
      spiritualForce: 'La santidad que abarca toda la realidad; el ciclo que se completa para renacer'
    },
    sefirah: {
      name: 'Jesed',
      hebrew: 'חֶסֶד',
      meaning: 'Misericordia / Bondad',
      divine: 'El — Dios todopoderoso',
      bodyPart: 'Brazo derecho',
      color: '#2563eb',
      qualities: ['Compasión oceánica', 'Intuición mística', 'Devoción total', 'Amor universal']
    },
    planet: { name: 'Júpiter', hebrew: 'צֶדֶק', sefirahConnection: 'Jesed' },
    element: 'Agua',
    qualities: ['Empatía profunda', 'Misticismo natural', 'Amor que todo lo abarca', 'Disolución en lo divino'],
    tikun: 'Mantener la identidad propia mientras se ama a todos; que la compasión no sea evasión de lo concreto'
  },

  13: { // Adar II (leap year) — same as Adar
    hebrewName: 'דָּגִים',
    transliteration: 'Dagim',
    spanishName: 'Piscis',
    symbol: '♓',
    tribe: {
      name: 'Naftalí',
      hebrew: 'נַפְתָּלִי',
      englishName: 'Naphtali',
      qualities: ['Velocidad espiritual', 'Elegancia', 'Gracia poética', 'Comunicación sublime'],
      stone: 'Amatista'
    },
    letter: {
      letter: 'ק',
      name: 'Kuf',
      numericalValue: 100,
      meaning: 'Mono, sagrado y profano, ciclo completo',
      spiritualForce: 'La santidad que abarca toda la realidad; el ciclo que se completa para renacer'
    },
    sefirah: {
      name: 'Jesed',
      hebrew: 'חֶסֶד',
      meaning: 'Misericordia / Bondad',
      divine: 'El — Dios todopoderoso',
      bodyPart: 'Brazo derecho',
      color: '#2563eb',
      qualities: ['Compasión oceánica', 'Intuición mística', 'Devoción total', 'Amor universal']
    },
    planet: { name: 'Júpiter', hebrew: 'צֶדֶק', sefirahConnection: 'Jesed' },
    element: 'Agua',
    qualities: ['Empatía profunda', 'Misticismo natural', 'Amor que todo lo abarca', 'Disolución en lo divino'],
    tikun: 'Mantener la identidad propia mientras se ama a todos; que la compasión no sea evasión de lo concreto'
  }
};

export const HEBREW_MONTH_NAMES: Record<number, { english: string; spanish: string; hebrew: string }> = {
  1:  { english: 'Nisan',    spanish: 'Nisán',     hebrew: 'נִיסָן' },
  2:  { english: 'Iyyar',    spanish: 'Iyar',      hebrew: 'אִיָּר' },
  3:  { english: 'Sivan',    spanish: 'Siván',     hebrew: 'סִיוָן' },
  4:  { english: 'Tamuz',    spanish: 'Tamuz',     hebrew: 'תַּמּוּז' },
  5:  { english: 'Av',       spanish: 'Av',        hebrew: 'אָב' },
  6:  { english: 'Elul',     spanish: 'Elul',      hebrew: 'אֱלוּל' },
  7:  { english: 'Tishrei',  spanish: 'Tishrei',   hebrew: 'תִּשְׁרֵי' },
  8:  { english: 'Cheshvan', spanish: 'Jeshvan',   hebrew: 'חֶשְׁוָן' },
  9:  { english: 'Kislev',   spanish: 'Kislev',    hebrew: 'כִּסְלֵו' },
  10: { english: 'Tevet',    spanish: 'Tevet',     hebrew: 'טֵבֵת' },
  11: { english: 'Shvat',    spanish: 'Shevat',    hebrew: 'שְׁבָט' },
  12: { english: 'Adar',     spanish: 'Adar',      hebrew: 'אֲדָר' },
  13: { english: 'Adar II',  spanish: 'Adar II',   hebrew: 'אֲדָר ב' },
};

export const HEBREW_DAY_NAMES: Record<number, string> = {
  1: 'Alef', 2: 'Bet', 3: 'Gimel', 4: 'Dalet', 5: 'Heh',
  6: 'Vav', 7: 'Zayin', 8: 'Jet', 9: 'Tet', 10: 'Yod',
  11: 'Yod-Alef', 12: 'Yod-Bet', 13: 'Yod-Gimel', 14: 'Yod-Dalet', 15: 'Tet-Vav',
  16: 'Tet-Zayin', 17: 'Tet-Jet', 18: 'Tet-Tet', 19: 'Yod-Tet', 20: 'Kaf',
  21: 'Kaf-Alef', 22: 'Kaf-Bet', 23: 'Kaf-Gimel', 24: 'Kaf-Dalet', 25: 'Kaf-Heh',
  26: 'Kaf-Vav', 27: 'Kaf-Zayin', 28: 'Kaf-Jet', 29: 'Kaf-Tet', 30: 'Lamed'
};

export function dayToHebrew(day: number): string {
  const units = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];
  const tens = ['', 'י', 'כ', 'ל'];
  if (day <= 9) return units[day];
  if (day === 10) return 'י';
  if (day === 15) return 'ט״ו';
  if (day === 16) return 'ט״ז';
  const t = Math.floor(day / 10);
  const u = day % 10;
  return (u > 0 ? tens[t] + '״' + units[u] : tens[t]);
}
